import { useCallback, useMemo, useRef, useState } from "react";
import { ExpandedState } from "@tanstack/react-table";
import { useOpenWorkRow, parseCloseId } from "@hooks/useOpenWorkRow";
import { View } from "@common/types/views/views";
import {
    ClientsWorkOverviewView,
    ClientsWorkOverviewViewSchema,
} from "@common/types/views/clientsWorkOverviewView";
import {
    WORKSPACE_USER_CACHE_KEYS,
    useWorkspaceUserCache,
} from "@hooks/useWorkspaceUserCache";
import { pluralize } from "@common/helpers/string";
import { Drawer } from "@design-system/Drawer";
import { Loader } from "@design-system/Loader/Loader";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { ViewTable, ViewTableHandle } from "@design-system/ViewTable/ViewTable";
import { CollapseAllButton } from "@design-system/ViewTable/CollapseAllButton";
import { ColumnSettings } from "@design-system/ViewToolbar/ColumnSettings";
import { Pagination } from "@design-system/Pagination/Pagination";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { CommandIslandTotal } from "@design-system/CommandIsland/CommandIslandTotal";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { useScope } from "@state/scopeContext.lib";
import { ClientsWorkOverviewCallbacks } from "@components/organizationView/clients-work/WorkRowActions";
import {
    useClientsCloseOverviewSlimQuery,
    useClientsCloseOverviewDetailsQuery,
} from "@api/workspace.api";
import {
    clientsWorkOverviewViewConfig,
    ClientsWorkOverviewRow,
    mapWorkspaceToWorkRow,
} from "@components/organizationView/clients-work/clientsWorkOverviewViewConfig";
import styles from "./CloseTemplateApplicationsDrawer.module.scss";
import { DEFAULT_APPLICATIONS_DRAWER_VIEW } from "./closeTemplateApplicationsDrawerView";

const DEFAULT_PAGE = 1;
// Matches the drawer's pre-pagination hardcoded limit — keeps the common
// case (<=100 applications) showing everything on page 1 as before, while
// now allowing users to page through the rest instead of silently truncating.
const PAGE_SIZE = 100;

interface Props {
    open: boolean;
    closeTemplateId: string | null;
    templateName: string | null;
    onClose: () => void;
}

const CloseTemplateApplicationsDrawerContent = ({
    closeTemplateId,
}: {
    closeTemplateId: string;
}) => {
    const { organizationId } = useScope();
    const openWorkRow = useOpenWorkRow();

    const [cachedView, setCachedView] =
        useWorkspaceUserCache<ClientsWorkOverviewView>(
            WORKSPACE_USER_CACHE_KEYS.CLOSE_TEMPLATE_APPLICATIONS_DRAWER_VIEW_CACHE_KEY,
            ClientsWorkOverviewViewSchema,
        );
    const [view, setView] = useState<ClientsWorkOverviewView>(
        cachedView ?? DEFAULT_APPLICATIONS_DRAWER_VIEW,
    );
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(DEFAULT_PAGE);
    const [expanded, setExpanded] = useState<ExpandedState>({});
    const tableRef = useRef<ViewTableHandle>(null);

    const handleViewChange = useCallback(
        (newView: ClientsWorkOverviewView) => {
            setView(newView);
            setCachedView(newView);
        },
        [setCachedView],
    );

    const handleViewChangeFromTable = useCallback(
        (newView: View) => {
            const parsed = ClientsWorkOverviewViewSchema.safeParse(newView);
            if (parsed.success) {
                handleViewChange(parsed.data);
            }
        },
        [handleViewChange],
    );

    const tableMeta = useMemo(
        () => ({
            callbacks: {
                onOpenWorkRow: openWorkRow,
            } satisfies ClientsWorkOverviewCallbacks,
        }),
        [openWorkRow],
    );

    const slimParams = useMemo(
        () => ({
            organizationId: organizationId ?? "",
            page,
            limit: PAGE_SIZE,
            filters: {
                closeTemplateIds: [closeTemplateId],
                search: search.trim() || undefined,
            },
        }),
        [organizationId, closeTemplateId, page, search],
    );

    const { data: slimData, isLoading: isSlimLoading } =
        useClientsCloseOverviewSlimQuery(slimParams);

    const workspaceIds = useMemo(
        () => slimData?.workspaces.map((w) => w.id) ?? [],
        [slimData],
    );

    const { data: detailsData } = useClientsCloseOverviewDetailsQuery(
        { organizationId: organizationId ?? "", workspaceIds },
        { enabled: workspaceIds.length > 0 },
    );

    const tableData = useMemo<ClientsWorkOverviewRow[] | null>(() => {
        if (isSlimLoading) {
            return null;
        }
        if (!slimData) {
            return [];
        }
        const detailsMap = new Map(
            (detailsData?.workspaces ?? []).map((ws) => [ws.id, ws]),
        );
        return slimData.workspaces.map((ws) => {
            const details = detailsMap.get(`workspace:${ws.id}`);
            if (details) {
                const temporaryValue = mapWorkspaceToWorkRow(details);
                return {
                    ...temporaryValue,
                    children: temporaryValue.children.filter(
                        (child) => child.closeTemplateId === closeTemplateId,
                    ),
                };
            }
            return {
                id: `workspace:${ws.id}`,
                rowType: "workspace" as const,
                name: ws.name,
                workspaceId: ws.id,
                children: [],
            } satisfies ClientsWorkOverviewRow;
        });
    }, [isSlimLoading, slimData, detailsData, closeTemplateId]);

    const handleRowClick = useCallback(
        (row: ClientsWorkOverviewRow | undefined) => {
            if (row?.rowType !== "work") {
                return;
            }
            const parsedId = parseCloseId(row.closeId);
            if (parsedId) {
                openWorkRow(row.workspaceId, parsedId);
            }
        },
        [openWorkRow],
    );

    const handleSearchChange = useCallback((value: string) => {
        setSearch(value);
        setPage(DEFAULT_PAGE);
    }, []);

    if (isSlimLoading) {
        return <Loader />;
    }

    return (
        <>
            <TablePageHeader
                isFullPage
                search={search}
                searchPlaceholder="Search by client or entity…"
                onSearchChange={handleSearchChange}
                borderBottom
            >
                <CollapseAllButton expanded={expanded} tableRef={tableRef} />
                <ColumnSettings
                    view={view}
                    viewConfig={clientsWorkOverviewViewConfig}
                    defaultView={DEFAULT_APPLICATIONS_DRAWER_VIEW}
                    onViewChange={handleViewChange}
                />
            </TablePageHeader>
            <ViewTable
                ref={tableRef}
                data={tableData}
                view={view}
                config={clientsWorkOverviewViewConfig}
                className={styles.applicationsView}
                onViewChange={handleViewChangeFromTable}
                expanded={expanded}
                onExpandedChange={setExpanded}
                emptyView={() => <EmptyState header="No applications found" />}
                loadingView={() => <Loader />}
                tableMeta={tableMeta}
                onRowClick={handleRowClick}
                autoExpandOnMount
                footer={
                    slimData && slimData.total > 0 ? (
                        <CommandIsland
                            sticky
                            className={
                                commandIslandStyles.bulkActionsStickyFooter
                            }
                        >
                            <CommandIslandTotal>
                                {pluralize("Application", slimData.total)}:{" "}
                                {slimData.total}
                            </CommandIslandTotal>
                            {slimData.pageCount > 1 && (
                                <Pagination
                                    size="md"
                                    pageCount={slimData.pageCount}
                                    currentPage={page}
                                    onPageChange={setPage}
                                />
                            )}
                        </CommandIsland>
                    ) : undefined
                }
            />
        </>
    );
};

export const CloseTemplateApplicationsDrawer = ({
    open,
    closeTemplateId,
    templateName,
    onClose,
}: Props) => (
    <Drawer
        open={open}
        onClose={onClose}
        direction="right"
        size="fullScreen"
        noPadding
        headerAddonLeft={
            templateName ? `Applications · ${templateName}` : "Applications"
        }
    >
        {open && closeTemplateId && (
            <CloseTemplateApplicationsDrawerContent
                closeTemplateId={closeTemplateId}
            />
        )}
    </Drawer>
);
